import React, { useMemo, useState } from 'react';

import { Alert, Box, Button, Paper, Typography } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

import NoRatesRing from './NoRatesRing';
import ScoreDonut from './ScoreDonut';
import ScorePill from './ScorePill';
import StatCard from './StatCard';
import SupplierFilterChips from './SupplierFilterChips';
import { recomputeAnalysis } from './analyzeParityCsv';
import { SECTION_SX, SEVERITY_COLORS } from './constants';
import { ParityAnalysis, RevenueHooks, TRACKED_OTAS } from './types';
import {
  computeImpressionParityScore,
  countDaysWithoutLeRates,
  isParityHealthy,
  parseCommaNumber,
  scoreToRating,
} from './utils';

interface SummarySectionProps {
  analysis: ParityAnalysis;
  revenueHooks: RevenueHooks | null;
  onGenerateEmail?: () => void;
  onExportPdf?: () => void;
}

export default function SummarySection({ analysis, revenueHooks, onGenerateEmail, onExportPdf }: SummarySectionProps) {
  const fmtPrice = (n: number) => currencyFormatter(analysis.currency, n, 2);
  const fmtPriceRounded = (v: number) => currencyFormatter(analysis.currency, v, 0);

  const impressionScore = revenueHooks ? computeImpressionParityScore(revenueHooks) : null;
  const showImpressionRing = impressionScore !== null && revenueHooks;

  const monthlyRevenueAtRisk = revenueHooks ? parseCommaNumber(revenueHooks.estimatedExtraRevenueMonthly) : 0;
  const showRevenueAtRiskBanner = Boolean(
    revenueHooks && Number.isFinite(monthlyRevenueAtRisk) && monthlyRevenueAtRisk !== 0,
  );

  const [summaryActiveSuppliers, setSummaryActiveSuppliers] = useState<Set<string>>(() => new Set(TRACKED_OTAS));
  const summaryAnalysis = useMemo(
    () => recomputeAnalysis(analysis, summaryActiveSuppliers),
    [analysis, summaryActiveSuppliers],
  );
  const sa = summaryAnalysis;
  const showDateBasedRing = sa.totalDates > 0;
  const isHealthy = isParityHealthy(sa);

  const toggleSummarySupplier = (ota: string) => {
    setSummaryActiveSuppliers((prev) => {
      if (prev.has(ota) && prev.size <= 1) {
        return prev;
      }
      const next = new Set(prev);
      if (next.has(ota)) {
        next.delete(ota);
      } else {
        next.add(ota);
      }
      return next;
    });
  };

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Box sx={{ mb: 3 }}>
        <SupplierFilterChips
          suppliers={TRACKED_OTAS}
          isActive={(ota) => summaryActiveSuppliers.has(ota)}
          onToggle={toggleSummarySupplier}
          label="Include Suppliers in Analysis"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Number(showDateBasedRing) + Number(!!showImpressionRing) + 1}, 1fr)`,
          gap: 3,
          mb: 2,
        }}
      >
        {showDateBasedRing && (
          <ScorePill
            ring={<ScoreDonut score={sa.parityScore} label="Date-based" />}
            rating={sa.parityRating}
            alertText={`${sa.parityRating}: ${sa.inParityCount} of ${sa.totalDates} comparable dates at parity`}
          />
        )}
        {showImpressionRing && (
          <ScorePill
            ring={<ScoreDonut score={impressionScore} label="Impressions" />}
            rating={scoreToRating(impressionScore)}
            alertText={`${scoreToRating(impressionScore)}: ${revenueHooks.impressionsOutOfParity} of all ${
              revenueHooks.totalImpressions
            } impressions out of parity`}
          />
        )}
        <NoRatesRing daysWithoutRates={countDaysWithoutLeRates(sa.dailyDetail)} />
      </Box>

      <Typography sx={{ color: 'text.secondary', fontSize: 14, textAlign: 'center', mb: 3 }}>
        {analysis.dateRangeStart} to {analysis.dateRangeEnd} &bull; {analysis.totalCalendarDays} calendar days
      </Typography>

      {!revenueHooks && (
        <Alert severity="info" sx={{ mb: 3 }} data-html2canvas-ignore>
          Impressions and revenue data not found for this property.
        </Alert>
      )}

      {isHealthy ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          This property is in good standing &mdash; prices are competitive across {sa.inParityCount} of {sa.totalDates}{' '}
          checked dates.
          {sa.avgPriceDiff > 0 && ` Average gap: ${fmtPrice(sa.avgPriceDiff)} (${sa.avgPriceDiffPct}%).`}
        </Alert>
      ) : (
        sa.totalDates > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
            <StatCard
              label="Avg Price Gap"
              value={fmtPrice(sa.avgPriceDiff)}
              subtitle={`${sa.avgPriceDiffPct}% above competitors`}
              fontSize={24}
            />
            <StatCard
              label="Worst Competitor"
              value={sa.worstCompetitor}
              subtitle={`Cheapest on ${sa.competitorCheapestCounts[sa.worstCompetitor] ?? 0} dates`}
              fontSize={18}
            />
            <StatCard
              label="Severe Issues"
              value={String(sa.severityCounts.severe ?? 0)}
              subtitle="Dates with >10% gap"
              valueColor={SEVERITY_COLORS.severe}
              fontSize={24}
            />
            <StatCard
              label="Out of Parity"
              value={String(sa.outOfParityCount)}
              subtitle={`of ${sa.totalDates} dates`}
              fontSize={24}
            />
          </Box>
        )
      )}

      {showRevenueAtRiskBanner && (
        <Box
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            p: 3,
            borderRadius: '12px',
            mb: 3,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'white' }}>
            ⚠ REVENUE AT RISK: {fmtPriceRounded(monthlyRevenueAtRisk)} per month
          </Typography>
          <Typography sx={{ fontSize: 14, opacity: 0.95, color: 'white' }}>
            {sa.outOfParityCount} days out of parity detected - {revenueHooks?.outOfParityPct}% of dates need attention
          </Typography>
        </Box>
      )}

      {(onGenerateEmail || onExportPdf) && (
        <Box data-html2canvas-ignore sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 4 }}>
          {onGenerateEmail && (
            <Button
              variant="contained"
              color="primary"
              sx={{ py: 2, borderRadius: '12px', fontSize: 15, fontWeight: 600 }}
              onClick={onGenerateEmail}
            >
              Generate Partner Email
            </Button>
          )}
          {onExportPdf && (
            <Button
              variant="contained"
              sx={{ py: 2, borderRadius: '12px', fontSize: 15, fontWeight: 600, bgcolor: 'grey.600' }}
              onClick={onExportPdf}
            >
              Export Report to PDF
            </Button>
          )}
        </Box>
      )}

      {revenueHooks && (
        <>
          <Typography sx={{ fontSize: 24, fontWeight: 600, mb: 3 }}>Your Conversion Impact</Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: 4,
              alignItems: 'center',
              p: 4,
              bgcolor: 'grey.50',
              borderRadius: '12px',
              mb: 3,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                when overpriced
              </Typography>
              <Typography sx={{ fontSize: 36, fontWeight: 700, color: 'error.main' }}>
                {revenueHooks.cvrOutOfParity}%
              </Typography>
            </Box>
            <Box sx={{ width: 2, height: 60, bgcolor: 'divider' }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                platform average at parity
              </Typography>
              <Typography sx={{ fontSize: 36, fontWeight: 700, color: 'success.main' }}>
                {revenueHooks.cvrAtParity}%
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 14, mb: 3 }}>
            Fixing parity could increase bookings by ~{revenueHooks.cvrImprovementPct}%
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
            <StatCard label="Search Impressions" value={revenueHooks.totalImpressions} subtitle="(last 30 days)" />
            <StatCard
              label="Shown Overpriced"
              value={`${revenueHooks.outOfParityPct}%`}
              subtitle={`(${revenueHooks.impressionsOutOfParity} impressions)`}
              valueColor={SEVERITY_COLORS.severe}
            />
            <StatCard label="Current Orders" value={String(revenueHooks.actualOrders30d)} subtitle="(last 30 days)" />
            <StatCard
              label="Current Revenue"
              value={fmtPriceRounded(parseCommaNumber(String(revenueHooks.actualRevenue30d)))}
              subtitle="(last 30 days)"
              fontSize={26}
            />
          </Box>
        </>
      )}
    </Paper>
  );
}
